import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../services/api';
import Layout from '../components/Layout';
import { showSuccess, showError, showConfirmDanger } from '../services/swal';

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [search, setSearch] = useState('');

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getCategories({ search });
            setCategories(res.data || []);
        } catch (err) {
            console.error("Failed to fetch categories", err);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchCategories();
        }, 300);
        return () => clearTimeout(debounce);
    }, [fetchCategories]);

    const handleSave = async () => {
        try {
            if (editingCategory) {
                await updateCategory(editingCategory.id, formData);
                showSuccess('Updated!', 'Category has been updated.');
            } else {
                await createCategory(formData);
                showSuccess('Created!', 'New category has been added.');
            }
            setShowModal(false);
            fetchCategories();
        } catch (err) {
            showError('Save Failed', err.response?.data?.message || err.message);
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({ name: category.name });
        setShowModal(true);
    };

    const handleAddNew = () => {
        setEditingCategory(null);
        setFormData({ name: '' });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await showConfirmDanger('Delete Category?', 'This will permanently remove this category and its associations.');
        if (result.isConfirmed) {
            try {
                await deleteCategory(id);
                fetchCategories();
                showSuccess('Deleted!', 'Category has been removed.');
            } catch (err) {
                showError('Delete Failed', err.response?.data?.message || err.message);
            }
        }
    };

    // UI Helpers
    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    const colors = ['#6C5DD3', '#FFA500', '#00C4DF', '#FF4B5C', '#27AE60', '#F2994A'];
    const getIconColor = (index) => colors[index % colors.length];

    return (
        <Layout title="Categories">
            {/* Summary Stats */}
            <div className="row g-4 mb-4">
                <div className="col-12 col-md-6 col-xl-4">
                    <div className="category-stat-card shadow-sm">
                        <div className="icon-badge bg-primary bg-opacity-10 text-primary">
                            <i className="bi bi-grid-fill"></i>
                        </div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Total Categories</small>
                            <h4 className="fw-bold mb-0">{categories.length}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-xl-4">
                    <div className="category-stat-card shadow-sm">
                        <div className="icon-badge bg-success bg-opacity-10 text-success">
                            <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Active Categories</small>
                            <h4 className="fw-bold mb-0">{categories.length}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-xl-4">
                    <div className="category-stat-card shadow-sm border-primary border-opacity-25 bg-primary bg-opacity-10">
                        <div className="icon-badge bg-primary text-white">
                            <i className="bi bi-plus-lg"></i>
                        </div>
                        <div className="flex-grow-1">
                            <h6 className="fw-bold mb-0">Need a new group?</h6>
                            <small className="text-primary-emphasis">Click to add a new category</small>
                        </div>
                        <button className="btn btn-primary rounded-pill px-3 fw-bold btn-sm" onClick={handleAddNew}>Add New</button>
                    </div>
                </div>
            </div>

            {/* Management Bar */}
            <div className="inventory-filter-bar border-0 bg-transparent shadow-none p-0 mb-4">
                <div className="row g-3 align-items-center">
                    <div className="col-12 col-md-6 col-lg-8">
                        <div className="search-wrapper position-relative">
                            <i className="bi bi-search search-icon" style={{ position: 'absolute', top: '50%', left: '1.25rem', transform: 'translateY(-50%)', zIndex: 10, color: '#6c757d' }}></i>
                            <input
                                type="text"
                                className="form-control pos-search-input"
                                placeholder="Search product categories..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ height: '52px', paddingLeft: '3.5rem', borderRadius: '14px' }}
                            />
                        </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-4 text-md-end">
                        <button className="btn btn-primary w-100 rounded-3 fw-bold d-flex align-items-center justify-content-center" onClick={handleAddNew} style={{ height: '52px' }}>
                            <i className="bi bi-plus-lg me-2"></i> Add Category
                        </button>
                    </div>
                </div>
            </div>

            {/* Categories Table */}
            <div className="card border-0 bg-transparent">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                    ) : categories.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table category-table align-middle">
                                <thead>
                                    <tr className="text-muted small text-uppercase fw-bold">
                                        <th className="ps-4">Category Identity</th>
                                        <th>Internal Name</th>
                                        <th className="text-end pe-4">Management Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((cat, index) => (
                                        <tr key={cat.id}>
                                            <td className="ps-4">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="icon-badge text-white" style={{ background: getIconColor(index) }}>
                                                        {getInitials(cat.name)}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold fs-6">{cat.name}</div>
                                                        <small className="text-muted">UID: #{cat.id.toString().padStart(4, '0')}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-normal">{cat.name} Group</span>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="d-flex justify-content-end gap-2">
                                                    <button className="btn btn-light border btn-sm rounded-circle p-0" style={{ width: '36px', height: '36px' }} onClick={() => handleEdit(cat)} title="Edit">
                                                        <i className="bi bi-pencil-fill text-primary"></i>
                                                    </button>
                                                    <button className="btn btn-light border btn-sm rounded-circle p-0" style={{ width: '36px', height: '36px' }} onClick={() => handleDelete(cat.id)} title="Delete">
                                                        <i className="bi bi-trash-fill text-danger"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-5 rounded-4 border empty-state-card">
                            <i className="bi bi-tag fs-1 opacity-25"></i>
                            <h5 className="mt-3">No categories found</h5>
                            <p className="text-muted">Add your first category to start organizing products.</p>
                            <button className="btn btn-primary rounded-pill px-4 mt-2" onClick={handleAddNew}>Add Category</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-primary text-white p-4">
                                    <h5 className="modal-title fw-bold">{editingCategory ? 'Modify Category' : 'Add New Category'}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="mb-4">
                                        <label className="form-label small fw-bold text-muted mb-2">Category Official Name</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><i className="bi bi-tag"></i></span>
                                            <input 
                                                type="text" 
                                                className="form-control rounded-end-3" 
                                                value={formData.name} 
                                                onChange={e => setFormData({ name: e.target.value })} 
                                                placeholder="e.g., Smartphones, Groceries, etc."
                                                style={{ height: '50px' }}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="form-text mt-2 small">This name will appear on the POS and reports.</div>
                                    </div>
                                    
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="d-flex align-items-center gap-2 text-muted small">
                                            <i className="bi bi-info-circle"></i>
                                            <span>Categories help you filter products and generate sales reports.</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => setShowModal(false)}>Discard</button>
                                    <button className="btn btn-primary px-4 rounded-pill fw-bold" onClick={handleSave}>
                                        {editingCategory ? 'Update Category' : 'Add Category'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <style>{`
                .category-stat-card {
                    background: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 20px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    transition: all 0.3s ease;
                }
                .icon-badge {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                }
                .category-table tbody tr {
                    border-bottom: 1px solid var(--border-color);
                    transition: all 0.2s ease;
                }
                .category-table tbody tr:hover {
                    background-color: var(--bg-color);
                }
                
                /* Dark Mode Overrides */
                [data-bs-theme="dark"] .bg-light {
                    background-color: rgba(255,255,255,0.05) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .modal-footer,
                [data-bs-theme="dark"] .modal-body .bg-light {
                    background-color: rgba(255,255,255,0.02) !important;
                }
                [data-bs-theme="dark"] .empty-state-card {
                    background-color: var(--card-bg) !important;
                    border-color: var(--border-color) !important;
                }
            `}</style>
        </Layout>
    );
};

export default Categories;
